import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Building2 } from "lucide-react";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import { publicApi } from "../lib/adminApi";

export default function CaseStudies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await publicApi.listCases({ page_size: 12 });
      if (res.ok) setItems(res.data.items);
      setLoading(false);
    })();
  }, []);

  return (
    <>
      <SEO
        title="Case Studies — Ridgehawk Logistics"
        description="Real outcomes for clients across the Greater Toronto Area: medical transport, dedicated routes, and B2B freight."
        path="/case-studies"
      />

      <section className="relative bg-ridge-charcoal text-white" data-testid="case-list-hero">
        <div className="container-pad py-20 md:py-24 lg:py-28">
          <SectionReveal>
            <div className="max-w-3xl">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-ridge-amber">Case Studies</span>
              <h1 className="font-display mt-4 text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Real outcomes. Real clients. Across the GTA.
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
                Operational impact, in numbers and in their words.
              </p>
            </div>
          </SectionReveal>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-ridge-amber" />
      </section>

      <section className="section-pad bg-white" data-testid="case-list-section">
        <div className="container-pad">
          {loading ? (
            <p className="text-sm text-ridge-muted">Loading case studies…</p>
          ) : items.length === 0 ? (
            <SectionReveal>
              <div className="mx-auto max-w-xl rounded-md border-2 border-dashed border-ridge-border bg-ridge-stone p-10 text-center">
                <span className="overline">Coming soon</span>
                <h2 className="font-display mt-3 text-2xl font-bold text-ridge-charcoal">First case studies in the works.</h2>
                <p className="mt-3 text-sm text-ridge-text">We're documenting recent client outcomes. Want to be one of them?</p>
                <Link to="/quote" className="btn-accent mt-6 inline-flex">Get a Quote <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </SectionReveal>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {items.map((c, idx) => {
                const headlineMetric = (c.metrics || [])[0];
                return (
                  <SectionReveal key={c.id} delay={idx * 0.05}>
                    <Link to={`/case-studies/${c.slug}`} className="group block h-full overflow-hidden rounded-md border border-ridge-border bg-white transition-all hover:-translate-y-0.5 hover:border-ridge-charcoal/25" data-testid={`case-card-${c.slug}`}>
                      <div className="aspect-[16/10] w-full overflow-hidden bg-ridge-stone">
                        {c.cover_image_url ? <img src={c.cover_image_url} alt={c.cover_image_alt || ""} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]" loading="lazy" /> : <div className="h-full w-full bg-gradient-to-br from-ridge-charcoal to-ridge-amber-dark" />}
                      </div>
                      <div className="p-6 md:p-7">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ridge-muted">
                          {c.client_industry ? <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{c.client_industry}</span> : null}
                          {c.client_location ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c.client_location}</span> : null}
                        </div>
                        <h3 className="font-display mt-3 text-xl font-semibold leading-snug text-ridge-charcoal group-hover:text-ridge-amber-dark">{c.title}</h3>
                        {c.excerpt ? <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-ridge-text">{c.excerpt}</p> : null}
                        {headlineMetric ? (
                          <div className="mt-5 rounded-md bg-ridge-stone px-4 py-3">
                            <div className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ridge-muted">{headlineMetric.label}</div>
                            <div className="font-display mt-0.5 text-2xl font-bold text-ridge-amber">{headlineMetric.value}</div>
                          </div>
                        ) : null}
                      </div>
                    </Link>
                  </SectionReveal>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
